import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Search, UserPlus, ChevronRight, CreditCard } from "lucide-react";
import axios from "axios";
import { AiAvatar, KIOSK_MESSAGES, resetGlobalSpeechState } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIOSK_ID = "kiosk_001";
const IDLE_TIMEOUT = 120; // seconds before returning to attract mode

export default function KioskWelcome() {
  const navigate = useNavigate();
  const [kioskConfig, setKioskConfig] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const idleTimerRef = useRef(null);

  useEffect(() => {
    fetchKioskConfig();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Reset global speech state when welcome page loads (new session)
  useEffect(() => {
    resetGlobalSpeechState();
  }, []);

  // Idle detection - return to attract mode after inactivity
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        navigate('/kiosk/attract');
      }, IDLE_TIMEOUT * 1000);
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'touchmove'];
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer, { passive: true });
    });

    resetIdleTimer(); // Start initial timer

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [navigate]);

  const fetchKioskConfig = async () => {
    try {
      const response = await axios.get(`${API}/public/kiosk/${KIOSK_ID}`);
      setKioskConfig(response.data);
    } catch (error) {
      setKioskConfig({
        kiosk_id: KIOSK_ID,
        kiosk_name: "ParkWise Kiosk",
        site_id: "site_001",
        site_name: "Town Centre Car Park"
      });
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F0FDF4 100%)' }} data-testid="kiosk-welcome">
      {/* Header */}
      <header className="flex items-center justify-between p-6 md:p-8">
        <ParkWiseLogo size="lg" />
        
        <div className="text-right">
          <p className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {formatTime(currentTime)}
          </p>
          <p className="text-sm md:text-base text-slate-500">
            {formatDate(currentTime)}
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 pb-12">
        <div className="w-full max-w-4xl animate-fade-in">
          {/* Avatar and Welcome Message */}
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-12">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <AiAvatar 
                message={KIOSK_MESSAGES.welcome} 
                size="large"
                autoSpeak={true}
              />
            </div>

            {/* Welcome Text */}
            <div className="text-center md:text-left">
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 leading-relaxed"
                style={{ 
                  fontFamily: 'Outfit, sans-serif',
                  lineHeight: '1.4',
                  paddingBottom: '0.25em'
                }}
              >
                Welcome to
                <span 
                  className="block mt-2"
                  style={{ 
                    background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    lineHeight: '1.5',
                    paddingBottom: '0.15em'
                  }}
                >
                  ParkWise
                </span>
                <span 
                  className="block mt-1 text-xl sm:text-2xl md:text-3xl font-semibold text-slate-700"
                  style={{ 
                    lineHeight: '1.5',
                    paddingBottom: '0.2em'
                  }}
                >
                  Designated Parking
                </span>
                <span 
                  className="block text-xl sm:text-2xl md:text-3xl font-semibold text-slate-700"
                  style={{ 
                    lineHeight: '1.5'
                  }}
                >
                  Space Management
                </span>
              </h2>
            </div>
          </div>

          {/* Action Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Check Registration */}
            <button
              onClick={() => navigate("/kiosk/lookup")}
              className="group bg-white rounded-3xl p-6 border-2 border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 text-left"
              data-testid="kiosk-check-registration"
            >
              <div className="flex flex-col gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors duration-300 group-hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(59, 130, 246, 0.2) 100%)' }}
                >
                  <Search className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Check Registration
                  </h3>
                  <p className="text-slate-500 mb-3">
                    View your parking status
                  </p>
                  <div className="flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-3 transition-all">
                    <span>Check Now</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </button>

            {/* Pay for Parking */}
            <button
              onClick={() => navigate("/kiosk/pay")}
              className="group bg-white rounded-3xl p-6 border-2 border-slate-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 text-left"
              data-testid="kiosk-pay-parking"
            >
              <div className="flex flex-col gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors duration-300 group-hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 139, 250, 0.2) 100%)' }}
                >
                  <CreditCard className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Pay for Parking
                  </h3>
                  <p className="text-slate-500 mb-3">
                    Local Connect discounts applied
                  </p>
                  <div className="flex items-center gap-2 text-purple-600 font-semibold group-hover:gap-3 transition-all">
                    <span>Pay Now</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </button>

            {/* New Registration */}
            <button
              onClick={() => navigate("/kiosk/category")}
              className="group bg-white rounded-3xl p-6 border-2 border-slate-200 hover:border-green-400 hover:shadow-xl transition-all duration-300 text-left"
              data-testid="kiosk-new-registration"
            >
              <div className="flex flex-col gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors duration-300 group-hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.2) 100%)' }}
                >
                  <UserPlus className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    New Registration
                  </h3>
                  <p className="text-slate-500 mb-3">
                    Register for designated parking
                  </p>
                  <div className="flex items-center gap-2 text-green-600 font-semibold group-hover:gap-3 transition-all">
                    <span>Register Now</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Help Text */}
          <p className="text-center text-slate-400 mt-8 text-lg">
            Tap the avatar above to hear the welcome message again.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-4 border-t border-slate-200 bg-white/50 backdrop-blur-sm">
        <div className="flex items-center justify-between text-sm text-slate-400">
          <span>Kiosk: {kioskConfig?.kiosk_name || KIOSK_ID}</span>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span>Online</span>
          </div>
          <span>Powered by ParkWise</span>
        </div>
      </footer>
    </div>
  );
}
